import React, { useState } from 'react';
import { X, Calendar as CalendarIcon, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { services } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Formules pour Nettoyage Automobile (Extérieur)
const formulesAutomobile = [
  { id: 'auto-standard', name: 'Formule Standard - 70€', price: '70€' },
  { id: 'auto-premium', name: 'Formule Haut de Gamme - 140€', price: '140€' }
];

// Formules pour Détailing Intérieur
const formulesInterieur = [
  { id: 'int-basse', name: 'Formule Basse - 70€', price: '70€' },
  { id: 'int-standard', name: 'Formule Standard - 110€', price: '110€' },
  { id: 'int-premium', name: 'Formule Premium - 150€', price: '150€' },
  { id: 'int-haut', name: 'Formule Haut de Gamme - 220€', price: '220€' }
];

const AppointmentForm = ({ onClose }) => {
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    formula: '',
    time: '',
    message: ''
  });

  const timeSlots = [
    '08:00', '09:00', '10:00', '11:00', '12:00', 
    '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error('Veuillez sélectionner une date');
      return;
    }

    // Check if formula is required
    const needsFormula = formData.service === 'Nettoyage Automobile' || formData.service === 'Détailing Intérieur';
    if (needsFormula && !formData.formula) {
      toast.error('Veuillez sélectionner une formule');
      return;
    }

    setLoading(true);

    try {
      const serviceWithFormula = formData.formula 
        ? `${formData.service} - ${formData.formula}`
        : formData.service;

      await axios.post(`${BACKEND_URL}/api/appointments`, {
        ...formData,
        service: serviceWithFormula,
        date: date.toISOString()
      });

      toast.success('Rendez-vous demandé! Nous vous confirmerons la disponibilité rapidement.', {
        duration: 4000
      });

      onClose();
    } catch (error) {
      console.error('Error submitting appointment:', error);
      toast.error('Erreur lors de la demande de rendez-vous. Veuillez réessayer.', {
        duration: 4000
      });
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleServiceChange = (value) => {
    setFormData({
      ...formData,
      service: value,
      formula: '' // Reset formula when service changes
    });
  };

  const handleFormulaChange = (value) => {
    setFormData({
      ...formData,
      formula: value
    });
  };

  const handleTimeChange = (value) => {
    setFormData({
      ...formData,
      time: value
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-yellow-500 to-yellow-400 p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
              <CalendarIcon className="w-6 h-6 text-gray-900" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Prendre Rendez-Vous</h2>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose}
            className="text-gray-900 hover:bg-white/20"
          >
            <X className="w-6 h-6" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="appt-name" className="text-gray-700 font-semibold mb-2 block">
                Nom complet *
              </Label>
              <Input
                id="appt-name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full border-gray-300 focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Jean Dupont"
              />
            </div>
            <div>
              <Label htmlFor="appt-phone" className="text-gray-700 font-semibold mb-2 block">
                Téléphone *
              </Label>
              <Input
                id="appt-phone"
                name="phone"
                type="tel"
                required
                value={formData.phone}
                onChange={handleChange}
                className="w-full border-gray-300 focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="06 12 34 56 78"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="appt-email" className="text-gray-700 font-semibold mb-2 block">
              Email *
            </Label>
            <Input
              id="appt-email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full border-gray-300 focus:border-yellow-500 focus:ring-yellow-500"
              placeholder="jean.dupont@example.com"
            />
          </div>

          <div>
            <Label htmlFor="appt-service" className="text-gray-700 font-semibold mb-2 block">
              Service souhaité *
            </Label>
            <Select required value={formData.service} onValueChange={handleServiceChange}>
              <SelectTrigger className="w-full border-gray-300 focus:border-yellow-500 focus:ring-yellow-500">
                <SelectValue placeholder="Sélectionnez un service" />
              </SelectTrigger>
              <SelectContent>
                {services.map((service) => (
                  <SelectItem key={service.id} value={service.title}>
                    {service.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Formula selection for Automobile or Détailing Intérieur */}
          {formData.service === 'Nettoyage Automobile' && (
            <div>
              <Label htmlFor="appt-formula" className="text-gray-700 font-semibold mb-2 block">
                Choisissez votre formule *
              </Label>
              <Select required value={formData.formula} onValueChange={handleFormulaChange}>
                <SelectTrigger className="w-full border-gray-300 focus:border-yellow-500 focus:ring-yellow-500">
                  <SelectValue placeholder="Sélectionnez une formule" />
                </SelectTrigger>
                <SelectContent>
                  {formulesAutomobile.map((formula) => (
                    <SelectItem key={formula.id} value={formula.name}>
                      {formula.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {formData.service === 'Détailing Intérieur' && (
            <div>
              <Label htmlFor="appt-formula" className="text-gray-700 font-semibold mb-2 block">
                Choisissez votre formule *
              </Label>
              <Select required value={formData.formula} onValueChange={handleFormulaChange}>
                <SelectTrigger className="w-full border-gray-300 focus:border-yellow-500 focus:ring-yellow-500">
                  <SelectValue placeholder="Sélectionnez une formule" />
                </SelectTrigger>
                <SelectContent>
                  {formulesInterieur.map((formula) => (
                    <SelectItem key={formula.id} value={formula.name}>
                      {formula.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label className="text-gray-700 font-semibold mb-2 block">
                Date souhaitée *
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal border-gray-300 hover:border-yellow-500"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, 'PPP', { locale: fr }) : 'Choisir une date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    locale={fr}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label htmlFor="appt-time" className="text-gray-700 font-semibold mb-2 block">
                Heure souhaitée *
              </Label>
              <Select required value={formData.time} onValueChange={handleTimeChange}>
                <SelectTrigger className="w-full border-gray-300 focus:border-yellow-500 focus:ring-yellow-500">
                  <SelectValue placeholder="Choisir l'heure" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((time) => (
                    <SelectItem key={time} value={time}>
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-2" />
                        {time}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="appt-message" className="text-gray-700 font-semibold mb-2 block">
              Message (optionnel)
            </Label>
            <Textarea
              id="appt-message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="w-full border-gray-300 focus:border-yellow-500 focus:ring-yellow-500 resize-none"
              placeholder="Informations complémentaires..."
            />
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              <strong>Note :</strong> Nous sommes disponibles 24h/24 et 7j/7. Votre demande sera confirmée dans les plus brefs délais.
            </p>
            <p className="text-sm text-orange-600 mt-2 font-medium">
              ⚠️ Ce rendez-vous est sous réserve de disponibilité du créneau choisi. Nous vous confirmerons par téléphone ou email si ce créneau est disponible.
            </p>
          </div>

          <div className="flex gap-4">
            <Button 
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1 border-2 border-gray-300 text-gray-700 font-semibold py-6"
            >
              Annuler
            </Button>
            <Button 
              type="submit"
              disabled={loading}
              className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold py-6 shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
            >
              <CalendarIcon className="w-5 h-5 mr-2" />
              {loading ? 'Envoi...' : 'Confirmer le RDV'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentForm;
